import { Col, Row, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Routes } from '../../../../app/routes';
import LayoutInner from '../../../../layouts/main/components/layout-inner/layout-inner';
import PageTitle from '../../../../shared/components/page-title/page-title';
import Table from '../../../../shared/components/table/table';
import { convertServerDateTimeString } from '../../../../shared/utils/format-date/format-date';
import AuthGuard from '../../../auth/components/auth-guard/auth-guard';
import { useGetUserQuery, useGetUserSettingsQuery, useGetUserTraceLogsQuery } from '../../api/user-api';
import { hasPermission } from '../../../../shared/utils/has-permission';
import { UserPermissionType } from '../../models/user-permission-type';
import { UserSettings } from '../../models/user-settings';
import styles from './current-user-trace-logs.module.css';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import { openModal } from '../../../ui/store/ui-slice';
import { exportTraceLogsModalId } from '../../../../shared/constants/constants';
import { ExportType } from '../../../../shared/components/export-modal/export-type';
import { useAppDispatch } from '../../../../store/hooks';
import ExportModal from '../../../../shared/components/export-modal/export-modal';

const CurrentUserTraceLogs = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['common', 'current-user']);
  const { data: userSettings } = useGetUserSettingsQuery();
  const { data: currentUser } = useGetUserQuery();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { data: currentUserTraceLogs } = useGetUserTraceLogsQuery({
    page: Number(searchParams.get('page')) || 1,
  });

  const currentUserTraceLogsDataSource = useMemo(() => {
    return (currentUserTraceLogs && Array.isArray(currentUserTraceLogs.items) ? currentUserTraceLogs.items : []).map(
      traceLog => ({
        key: traceLog.id,
        user: traceLog.user?.name,
        ipAddress: traceLog.ipAddress,
        customer: traceLog.customer?.name,
        action: traceLog.action,
        link: traceLog,
        document: traceLog.signRequest?.documents[0]?.name || '-',
        data: traceLog.data ? JSON.stringify(traceLog.data) : '',
        actionAt: convertServerDateTimeString(traceLog.actionAt, userSettings?.[UserSettings.DateFormat]),
      }),
    );
  }, [currentUserTraceLogs, userSettings]);

  const columns: ColumnsType<(typeof currentUserTraceLogsDataSource)[number]> = [
    {
      title: t('label.user'),
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: t('label.customer'),
      dataIndex: 'customer',
      key: 'customer',
      hidden: !(currentUser && hasPermission(currentUser, UserPermissionType.ViewCustomers)),
    },
    {
      title: t('label.action'),
      dataIndex: 'action',
      key: 'action',
    },
    {
      title: t('label.time'),
      dataIndex: 'actionAt',
      key: 'actionAt',
    },
    {
      title: t('label.link'),
      dataIndex: 'link',
      key: 'link',
      render: record => {
        if (record.scanRequest) {
          return (
            <Link
              to={Routes.ScanRequest.replace(':scanRequestId', record.scanRequest.id).replace(
                ':requestId',
                record.scanRequest.request.id,
              )}
              state={{ from: location }}
            >
              {record.scanRequest.name || t('label.scanRequest')}
            </Link>
          );
        } else if (record.signRequest) {
          return (
            <Link
              to={Routes.SignRequest.replace(':signRequestId', record.signRequest.id).replace(
                ':requestId',
                record.signRequest.request.id,
              )}
              state={{ from: location }}
            >
              {record.signRequest.name || t('label.signRequest')}
            </Link>
          );
        } else {
          return <Typography.Text>-</Typography.Text>;
        }
      },
    },

    {
      title: t('label.document'),
      dataIndex: 'document',
      key: 'document',
    },
    {
      title: t('label.ipAddress'),
      dataIndex: 'ipAddress',
      key: 'ipAddress',
    },
    {
      title: t('label.data'),
      dataIndex: 'data',
      key: 'data',
    },
  ];

  return (
    <AuthGuard permission={UserPermissionType.ViewTracelogs}>
      <Helmet>
        <title>{t('title', { ns: 'current-user' })}</title>
      </Helmet>
      <LayoutInner>
        <Row justify="space-between" className={styles.headerRow}>
          <Col>
            <PageTitle>{t('title', { ns: 'current-user' })}</PageTitle>
          </Col>
          <Col>
            <CustomButton
              className={styles.exportBtn}
              type="primaryLight"
              onClick={() =>
                dispatch(
                  openModal({
                    id: exportTraceLogsModalId,
                    props: {
                      type: ExportType.User,
                    },
                  }),
                )
              }
            >
              {t('action.export')}
            </CustomButton>
          </Col>
        </Row>
        <Table
          tableProps={{
            pagination: {
              showQuickJumper: true,
              position: ['topRight'],
            },
            dataSource: currentUserTraceLogsDataSource,
            columns,
          }}
        />
      </LayoutInner>
      <ExportModal />
    </AuthGuard>
  );
};

export default CurrentUserTraceLogs;
